import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { CustomerModel } from 'src/app/models/customer-model';
import { WebservicesService } from '../webservices/webservices.service';
import { filter, map } from 'rxjs/operators';
import type { ILoginResponsePayload } from 'src/app/interfaces/authentication.interface';
import { Platform } from '@ionic/angular';
import { ConfigService } from '../config/config.service';

export enum eMfaStatuses {
  required = 'required',
  passed = 'passed',
  unknown = 'unknown'
}

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>
export type TUserStatus = PartialBy<ILoginResponsePayload,  'lastIPAddress' | 'lastLogoutReason'| 'deviceId'> | null


@Injectable({
  providedIn: 'root',
})
export class UserSessionService {
  sessionToken: string;
  customer: CustomerModel;
  _sessionTokenSubject: Subject<string|null> = new Subject();
  _customerSubject: BehaviorSubject<CustomerModel>;
  _accounts: any;
  _userStatus: Subject<TUserStatus> = new Subject();
  _authStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  customerIPAddress: any;
  customerId: any;
  private _mfaStatus$:BehaviorSubject<eMfaStatuses>;
  mfaEnabled: boolean = false;

  constructor(
    private webservices: WebservicesService,
    private platform: Platform,
    private config: ConfigService
  ){
    this._customerSubject = new BehaviorSubject(undefined);
    this._mfaStatus$ = new BehaviorSubject(eMfaStatuses.unknown);
  }

  public setSessionToken(sessionToken: string) {
    this.sessionToken = sessionToken;
    if (sessionToken) {
      localStorage.setItem('userSession.sessionToken', sessionToken);
      sessionStorage.setItem('userSession.sessionToken', sessionToken);
    } else {
      localStorage.removeItem('userSession.sessionToken');
      sessionStorage.removeItem('userSession.sessionToken');
    }

    this._sessionTokenSubject.next(this.sessionToken);
  }

  public setCustomerIPAddress(IPAddress: any) {
    localStorage.setItem('lastIPAddress', IPAddress);
    this.customerIPAddress = IPAddress;
  }

  public getSessionToken() {
    if (this.sessionToken) {
      return this.sessionToken;
    }
    const allowPreviousDisconnect = this.config.getConfig().FEATURE_ALLOW_PREVIOUS_DISCONNECTION || true;
    const sessionToken = (this.platform.is('capacitor') && allowPreviousDisconnect) ? 
                            sessionStorage.getItem('userSession.sessionToken') : 
                            localStorage.getItem('userSession.sessionToken');  
      if (sessionToken) {
        this.setSessionToken(sessionToken);
    }

    return this.sessionToken;
  }

  public getCustomerIPAddress() {
    return this.customerIPAddress;
  }

  refreshCustomer() {
    const response = new Subject();
    if (this.sessionToken) {
      this.webservices
      .get('authentication/get-account', { session_token: this.sessionToken })
      .subscribe((resp: PlatformResponse<any>) => {
        if (resp.result && resp.payload && resp.payload.customer) {
          this.setCustomer(resp.payload.customer);
          response.next(resp.payload.customer);
          response.complete();
        }
      });
    } else {
      response.next(null);
      response.complete();
    }
    return response;
  }

  updateAccount(customer_id: string, newProps: any): Promise<void> {
    return new Promise((resolve, error) => {
      newProps.session_token = this.sessionToken;
      newProps.customer_id = customer_id;
      //
      console.warn('user-session.ts: updateAccount() setting');
      this.webservices
        .post('authentication/update-account', newProps)
        .subscribe({
          next: () => {
            resolve();
          },
          error: (err: any) => {
            error(err);
          },
          complete: () => {
          }
        });
    });
  }

  acceptTerms(customerId) {
    const obj: any = {
      customer_id: customerId,
      session_token: this.sessionToken,
    };

    return this.webservices.post('customer/accept-terms', obj);
  }

  acceptPrivacyPolicy(customerId) {
    const obj: any = {
      customer_id: customerId,
      session_token: this.sessionToken,
    };

    return this.webservices.post('customer/accept-privacy-policy', obj);
  }

  acceptHouseRules(customerId) {
    const obj: any = {
      customer_id: customerId,
      session_token: this.sessionToken,
    };

    return this.webservices.post('customer/accept-house-rules', obj);
  }

  public async clear() {
    localStorage.removeItem('userSession.sessionToken');
    localStorage.removeItem('userSession.customer');
    this.setUserStatus(null);
    //clearing loginResponse storage on logout. We should refine code to remove localStorage to clear all.
    // localStorage.clear()
    localStorage.removeItem('userSession.loginInfo');
    localStorage.removeItem('userSession.mfa');
    localStorage.removeItem('strongAuthenticationRequired');
    localStorage.removeItem('clientApp');
    localStorage.removeItem('lastIPAddress');
    localStorage.removeItem('customerID');
    localStorage.removeItem('reconfirmPersonalDetails');
    localStorage.removeItem('userSession.MFAApproved');

    this.setSessionToken(null);

    setTimeout(() => {
      this.setCustomer(null);
    }, 1000);
  }

  public async isAuthenticated(): Promise<boolean> {
    return await !!this.sessionToken;
  }

  public setCustomer(customer) {
    // store json data to keep storage space minimal
    if (customer){
      localStorage.setItem('userSession.customer', JSON.stringify(customer));
      sessionStorage.setItem('userSession.customer', JSON.stringify(customer));
      this.customer = new CustomerModel(customer);
    } else {
      localStorage.removeItem('userSession.customer');
      sessionStorage.removeItem('userSession.customer');
      this.customer = customer;
    }
    this._customerSubject.next(this.customer);
  }

  public getCustomer() {
    return this._customerSubject;
  }

  public getUserStatus() {
     return this._userStatus;
  }

  public setUserStatus(status:TUserStatus) {
    if (status && status.sessionToken) {
      this.setSessionToken(status.sessionToken);
      this._authStatus.next(true);
    }
    this._userStatus.next(status);
  }

  public getAuthenticationStatus(): Observable<boolean> {
    return this._authStatus.asObservable();
  }
  public async getCustomerSubscription() {
    if (this.customer) {
      setTimeout(() => {
        this._customerSubject.next(this.customer);
      }, 200);
    }
    const allowPreviousDisconnect = this.config.getConfig().FEATURE_ALLOW_PREVIOUS_DISCONNECTION || true;
    const customer: CustomerModel = (this.platform.is('capacitor') && allowPreviousDisconnect) ? 
                                      JSON.parse(sessionStorage.getItem('userSession.customer')) : 
                                      JSON.parse(localStorage.getItem('userSession.customer'));  
    if (customer) {
      setTimeout(() => {
        this.setCustomer(customer);
      }, 200);
    }

    return this._customerSubject;
  }

  public unsubscribe() {
    this._customerSubject.complete();
  }

  public setCustomerId(customerId: any){
    this.customerId = customerId
  }

  public getCustomerId(){
    return this.customerId
  }

  setMFAStatus(status:eMfaStatuses):void {
    localStorage.setItem('userSession.mfa', status === eMfaStatuses.required? 'true' : 'false');
    this._mfaStatus$.next(status);
  }

  setMFAApproval(status: string) {
    localStorage.setItem('userSession.MFAApproved', status);
  }

  getMFAApproval() {
    let mfa = localStorage.getItem('userSession.MFAApproved');
    return mfa;
  }

  isAuthenticatedWithMFA$(): Observable<boolean> {
    // need to unsubscribe when subscribe it
    return this._mfaStatus$.pipe(
      map( mfaStatus => !!this.sessionToken && mfaStatus != eMfaStatuses.required),
      filter( auth => !!auth )
    );
  }

  setLoginResponseForMFA(){
    this.mfaEnabled = true;
  }

  getLoginResponseForMFA(){
    return this.mfaEnabled;
  }
}
